import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** iter55g47 — Admin recovery email.
 *
 *  Recipient inbox for `/api/admin/password-reset/request`. When set,
 *  the "Forgot password?" link on the admin sign-in card emails a
 *  time-limited reset link here. Enumeration-safe: the reset endpoint
 *  returns 200 regardless of whether this is configured, so an
 *  unauthenticated caller can never learn whether recovery is armed.
 */
export default function AdminRecoveryEmailConfig({ cfg, onSaved }) {
  const [email, setEmail] = useState(cfg?.admin_recovery_email || '');
  const [busy, setBusy]   = useState(false);
  useEffect(() => { setEmail(cfg?.admin_recovery_email || ''); }, [cfg?.admin_recovery_email]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ admin_recovery_email: email.trim() });
      toast.success(email ? 'Recovery email saved.' : 'Recovery email cleared.');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save recovery email.');
    } finally { setBusy(false); }
  };

  const isConfigured = Boolean(cfg?.admin_recovery_email);

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-recovery-email">
      <div className="flex items-start gap-2">
        <Mail className="mt-0.5 h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Admin Recovery Email</div>
          <div className="text-[11px] text-muted-foreground">
            Where a "Forgot password?" reset link is sent from the admin sign-in
            card. Link expires in 60 minutes and can only be used once.
            {' '}
            {isConfigured
              ? <span className="text-success">Currently configured.</span>
              : <span className="text-warning-foreground">Not configured — the Forgot-password button silently no-ops.</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <div className="flex-1 max-w-md">
          <Label className="text-[11px]">Recovery email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@yourdomain.com"
            className="mt-1 h-8 text-[11.5px]"
            data-testid="admin-recovery-email-input"
          />
        </div>
        <div className="flex items-end">
          <Button
            size="sm"
            onClick={save}
            disabled={busy}
            className="h-8 gap-1.5"
            data-testid="admin-recovery-email-save"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
